import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Input, Modal, Select, Space, Spin, Timeline } from 'antd';
import {
  applyTaskCancel, getTaskCancelDetail, processTaskCancel, TASK_CANCEL_NAMES,
  type TaskCancelDetail,
} from '@/services/taskCancel';

/** 两个网页入口共用详情及办理弹窗，所有可操作性以服务端返回为准。 */
interface TaskCancelModalProps {
  /** 正式任务ID；为空关闭弹窗。 */
  taskId?: string | number;
  /** 指定历史申请轮次；省略时读取最新轮次。 */
  applicationId?: string;
  /** 关闭弹窗。 */
  onClose: () => void;
  /** 成功流转后刷新当前列表及角标。 */
  onSuccess: () => void;
}

/** 申请、审批和历史共用一个弹窗；过期请求失败时刷新资格，保留本次输入。 */
export default function TaskCancelModal({ taskId, applicationId, onClose, onSuccess }: TaskCancelModalProps) {
  const { message } = App.useApp();
  const [detail, setDetail] = useState<TaskCancelDetail>();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [approver, setApprover] = useState<string>();
  const [comment, setComment] = useState('');

  /** 切换任务时丢弃旧请求结果，防止慢响应覆盖新任务。 */
  useEffect(() => {
    let disposed = false;
    setDetail(undefined); setReason(''); setApprover(undefined); setComment(''); setError('');
    if (taskId == null) return () => { disposed = true; };
    setLoading(true);
    getTaskCancelDetail(taskId, applicationId).then(value => {
      if (!disposed) { setDetail(value); setApprover(value.approvers[0]?.id); }
    }).catch(cause => { if (!disposed) setError(cause instanceof Error ? cause.message : '详情加载失败'); })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [taskId, applicationId]);

  /** 发起或办理当前工作流；失败不关闭，允许查看提示后重试。 */
  async function submit(agree?: boolean): Promise<void> {
    if (!detail || taskId == null || busy) return;
    if (agree == null && (!reason.trim() || !approver)) { message.warning('请填写取消原因并选择部门经理'); return; }
    if (agree === false && detail.canApprove && !comment.trim()) { message.warning('请填写驳回意见'); return; }
    setBusy(true); setError('');
    try {
      const value = agree == null
        ? await applyTaskCancel(taskId, reason.trim(), approver!)
        : await processTaskCancel(detail, agree, comment.trim());
      setDetail(value); setComment('');
      message.success(value.status === 'CANCELLED' ? '任务已取消' : '操作成功');
      onSuccess();
      window.dispatchEvent(new Event('focus'));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '操作失败，请刷新重试');
      try { setDetail(await getTaskCancelDetail(taskId, applicationId)); } catch (refreshError) { console.warn('刷新取消详情失败', refreshError); }
    } finally { setBusy(false); }
  }

  return <Modal open={taskId != null} title="任务取消" onCancel={busy ? undefined : onClose} footer={null} destroyOnHidden>
    <Spin spinning={loading}>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {detail && <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <strong>{detail.status ? TASK_CANCEL_NAMES[detail.status] : '申请取消'}</strong>
        {detail.reason && <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>取消原因：{detail.reason}</div>}
        {detail.deadline && <div>客户确认截止：{new Date(Number(detail.deadline)).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}</div>}
        {detail.status && detail.status !== 'REJECTED' && detail.status !== 'CANCELLED'
          && <Alert type="info" message="取消处理期间，任务及询价复核暂停办理。" />}
        {detail.canApply && <>
          <label htmlFor="task-cancel-reason">取消原因（必填）</label>
          <Input.TextArea id="task-cancel-reason" value={reason} onChange={e => setReason(e.target.value)}
            maxLength={1000} showCount rows={4} disabled={busy} />
          <label htmlFor="task-cancel-manager">部门经理（必选，可选择具备资格的本人）</label>
          <Select id="task-cancel-manager" value={approver} onChange={setApprover} style={{ width: '100%' }}
            options={detail.approvers.map(p => ({ value: String(p.id), label: p.name }))} disabled={busy} />
          {!detail.approvers.length && <Alert type="warning" message="没有同机构有效部门经理，暂不能提交。" />}
          <Button danger type="primary" loading={busy} disabled={!detail.approvers.length} onClick={() => submit()}>提交取消申请</Button>
        </>}
        {!detail.canApply && !detail.applicationId && <Alert type="info" message={detail.unavailableReason} />}
        {(detail.canApprove || detail.canConfirm) && <>
          <Input.TextArea value={comment} onChange={e => setComment(e.target.value)} maxLength={1000}
            placeholder={detail.canApprove ? '审批意见（驳回时必填）' : '确认意见（可选）'} disabled={busy} />
          <Space>
            <Button loading={busy} onClick={() => submit(false)}>{detail.canApprove ? '驳回取消' : '不同意'}</Button>
            <Button type="primary" danger loading={busy} onClick={() => submit(true)}>{detail.canApprove ? '同意取消' : '同意取消'}</Button>
          </Space>
        </>}
        {!!detail.records?.length && <Timeline items={detail.records.map((record, index) => ({
          key: record.id || index, label: record.createTime, children: <><strong>{record.creator?.name || '系统'} · {record.actionName}</strong><div style={{ whiteSpace: 'pre-wrap' }}>{record.comment}</div></>,
        }))} />}
      </Space>}
    </Spin>
  </Modal>;
}
